import { RuleService } from '../../services/rule.ts';
import { DomainService } from '../../services/domain.ts';
import { PluginService } from '../../services/plugin.ts';
import { OrganizationService } from '../../services/organization.ts';
import { ToastService } from '../../services/toast.ts';
import { Rule, Domain, Plugin } from '../../models/types.ts';
import { processDestinations } from '../../utils/backend-routing.ts';
// import * as yaml from 'js-yaml'; // Removed

import '../../components/library/form/form-input.ts';
import '../../components/library/form/form-toggle.ts';
import '../../components/library/form/form-checkbox-group.ts';
import '../../components/library/form/form-dynamic-list.ts';
import '../../components/library/form/form-select.ts';
import '../../components/library/ui/app-icon.ts';
import '../../components/library/ui/app-modal.ts';
import '../../components/library/ui/app-button.ts';
import '../../components/library/ui/app-badge.ts';
import '../../components/library/ui/app-save-bar.ts';
import '../../components/library/ui/code-editor.ts';
import '../../components/library/form/form-plugin-list.ts';
import { injectAesthetic } from '../../components/library/ui/shared-styles.ts';

export class RuleManagement extends HTMLElement {
    private ruleService = RuleService.getInstance();
    private domainService = DomainService.getInstance();
    private pluginService = PluginService.getInstance();
    private orgService = OrganizationService.getInstance();
    private toastService = ToastService.getInstance();
    private rules: Rule[] = [];
    private allPlugins: Plugin[] = [];
    private requestPlugins: Plugin[] = [];
    private responsePlugins: Plugin[] = [];
    private domain: Domain | null = null;
    private domainId: string | null = null;
    private isLoading = false;
    private viewMode: 'list' | 'map' | 'code' = 'map';
    private showModal = false;
    private editMode = false;
    private editingRuleId: string | null = null;
    private currentYaml: string = "";
    private originalYaml: string = "";
    private seed = Math.floor(Math.random() * 10000);

    // Interactive Map State
    private scale = 1;
    private panning = false;
    private startX = 0;
    private startY = 0;
    private pointX = 0;
    private pointY = 0;

    // Bound event listeners
    private handleMouseMoveBound = this.handleMouseMove.bind(this);
    private handleMouseUpBound = this.handleMouseUp.bind(this);

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
    }

    static get observedAttributes() {
        return ['domain-id'];
    }

    attributeChangedCallback(name: string, oldValue: string, newValue: string) {
        if (name === 'domain-id' && oldValue !== newValue) {
            this.domainId = newValue;
            this.loadData();
        }
    }

    connectedCallback() {
        this.render();
        // Add global event listeners for drag end (in case mouse leaves container)
        window.addEventListener('mousemove', this.handleMouseMoveBound);
        window.addEventListener('mouseup', this.handleMouseUpBound);
    }

    disconnectedCallback() {
        window.removeEventListener('mousemove', this.handleMouseMoveBound);
        window.removeEventListener('mouseup', this.handleMouseUpBound);
    }

    private handleMouseMove(e: MouseEvent) {
        if (!this.panning) return;
        e.preventDefault();

        const svg = this.shadowRoot?.getElementById('mapSvg') as unknown as SVGSVGElement;
        if (!svg) return;

        const CTM = svg.getScreenCTM();
        if (!CTM) return;
        const inv = CTM.inverse();

        const p1 = svg.createSVGPoint();
        p1.x = this.startX;
        p1.y = this.startY;

        const p2 = svg.createSVGPoint();
        p2.x = e.clientX;
        p2.y = e.clientY;

        const svgP1 = p1.matrixTransform(inv);
        const svgP2 = p2.matrixTransform(inv);

        this.pointX += (svgP2.x - svgP1.x);
        this.pointY += (svgP2.y - svgP1.y);

        this.startX = e.clientX;
        this.startY = e.clientY;

        this.updateTransform();
    }

    private handleMouseUp() {
        this.panning = false;
        const container = this.shadowRoot?.querySelector('.map-container') as HTMLElement;
        if (container) container.style.cursor = 'grab';
    }

    private updateTransform() {
        const content = this.shadowRoot?.getElementById('mapContent');
        if (content) {
            content.setAttribute('transform', `translate(${this.pointX}, ${this.pointY}) scale(${this.scale})`);
        }
    }

    async loadData() {
        if (!this.domainId || this.isLoading) return;
        this.isLoading = true;
        this.render();
        try {
            const orgId = localStorage.getItem('crate_org_id') || '';
            const [rules, domain, plugins] = await Promise.all([
                this.ruleService.listByDomain(this.domainId),
                this.domainService.get(this.domainId),
                this.pluginService.listPlugins(orgId)
            ]);
            this.rules = (rules as any[] || []).sort((a, b) => (a.path || '').localeCompare(b.path || ''));
            this.domain = domain as any;
            this.allPlugins = (plugins as Plugin[]) || [];

            if (this.viewMode === 'code') {
                // Fetch valid YAML from backend (includes URNs)
                this.currentYaml = await this.domainService.getConfig(this.domainId);
                this.originalYaml = this.currentYaml;
            }
        } catch (e: any) {
            this.rules = [];
            this.allPlugins = [];
            this.toastService.error(e.message || 'Failed to load data');
        } finally {
            this.isLoading = false;
            this.render();
        }
    }

    // generateLocalYaml removed as we now fetch from backend


    async handleCreateRule() {
        if (!this.shadowRoot) return;
        try {
            const name = (this.shadowRoot.querySelector('form-input[name="name"]') as any).value;
            const path = (this.shadowRoot.querySelector('form-input[name="path"]') as any).value;
            const methods = (this.shadowRoot.querySelector('form-checkbox-group[name="methods"]') as any).selectedValues;
            const active = (this.shadowRoot.querySelector('form-toggle[name="active"]') as any).checked;
            // Destinations now returns either string[] or {value, weight}[]
            const rawDestinations = (this.shadowRoot.querySelector('form-dynamic-list[name="destination"]') as any).values;
            const { destinations, weights, limits, mappings } = processDestinations(rawDestinations);
            const enableRateLimit = (this.shadowRoot.querySelector('form-toggle[name="enableRateLimit"]') as any).checked;

            if (!name || !path) {
                this.toastService.warning('Name and Path are required.');
                return;
            }

            if (methods.length === 0) {
                this.toastService.warning('At least one HTTP method must be selected.');
                return;
            }

            const routingStrategy = (this.shadowRoot.querySelector('form-select[name="routingStrategy"]') as any).value;
            let routingConfig: any = {};

            if (routingStrategy === 'header' || routingStrategy === 'header_match') {
                const headerName = (this.shadowRoot.querySelector('form-input[name="config_header_name"]') as any).value;
                if (headerName) routingConfig['header_name'] = headerName;
            } else if (routingStrategy === 'waterfall') {
                const reset = (this.shadowRoot.querySelector('form-select[name="config_waterfall_reset"]') as any).value;
                if (reset) routingConfig['limit_reset'] = reset;
            }

            let details: any = {
                routing: {
                    type: routingStrategy,
                    destinations: destinations,
                    config: Object.keys(routingConfig).length > 0 ? routingConfig : undefined,
                    weights: Object.keys(weights).length > 0 ? weights : undefined,
                    limits: Object.keys(limits).length > 0 ? limits : undefined,
                    mappings: Object.keys(mappings).length > 0 ? mappings : undefined
                }
            };

            if (enableRateLimit) {
                const limit = parseInt((this.shadowRoot.querySelector('form-input[name="rateLimit"]') as any).value);
                const rate = (this.shadowRoot.querySelector('form-select[name="rateWindow"]') as any).value;
                const identifierType = (this.shadowRoot.querySelector('form-select[name="identifierType"]') as any).value;
                const identifier = (this.shadowRoot.querySelector('form-input[name="identifier"]') as any).value;

                if (isNaN(limit) || limit <= 0) {
                    this.toastService.warning('Invalid rate limit. Must be a positive number.');
                    return;
                }

                    const multiplier = parseInt((this.shadowRoot.querySelector('form-input[name="multiplier"]') as any).value) || 1;
                    details.rate_limit = {
                        limit: limit,
                        rate: rate,
                        multiplier: multiplier,
                        identifier_type: identifierType,
                        identifier: identifier
                    };
            }

            const enableRetry = (this.shadowRoot.querySelector('form-toggle[name="enableRetry"]') as any).checked;
            let retryPolicy = undefined;
            if (enableRetry) {
                const retryOnMethods = (this.shadowRoot.querySelector('form-checkbox-group[name="retryOnMethods"]') as any).selectedValues;
                retryPolicy = {
                    enabled: true,
                    retry_on_methods: retryOnMethods
                };
            }

            if (retryPolicy) {
                details.retry_policy = retryPolicy;
            }

            // Sync plugins atomically via backend
            const inboundList = this.shadowRoot.querySelector('form-plugin-list[name="inbound_plugins"]') as any;
            const outboundList = this.shadowRoot.querySelector('form-plugin-list[name="outbound_plugins"]') as any;

            details.inbound_plugins = (inboundList?.values || []).filter((p: any) => p.plugin_id !== "");
            details.outbound_plugins = (outboundList?.values || []).filter((p: any) => p.plugin_id !== "");

            const newRule: Rule = {
                name,
                path,
                methods,
                domain_id: this.domainId,
                active,
                details
            };

            await this.ruleService.create(newRule as any);

            this.showModal = false;
            await this.loadData();
        } catch (e: any) {
            this.toastService.error(`Failed to create rule: ${e.message}`);
        }
    }

    async handleUpdateRule() {
        if (!this.editingRuleId || !this.shadowRoot) return;
        try {
            const name = (this.shadowRoot.querySelector('form-input[name="name"]') as any).value;
            const path = (this.shadowRoot.querySelector('form-input[name="path"]') as any).value;
            const methods = (this.shadowRoot.querySelector('form-checkbox-group[name="methods"]') as any).selectedValues;
            const active = (this.shadowRoot.querySelector('form-toggle[name="active"]') as any).checked;
            // Destinations now returns either string[] or {value, weight}[]
            const rawDestinations = (this.shadowRoot.querySelector('form-dynamic-list[name="destination"]') as any).values;
            const { destinations, weights, limits, mappings } = processDestinations(rawDestinations);
            const enableRateLimit = (this.shadowRoot.querySelector('form-toggle[name="enableRateLimit"]') as any).checked;

            if (!name || !path) {
                this.toastService.warning('Name and Path are required.');
                return;
            }

            if (methods.length === 0) {
                this.toastService.warning('At least one HTTP method must be selected.');
                return;
            }

            const routingStrategy = (this.shadowRoot.querySelector('form-select[name="routingStrategy"]') as any).value;
            let routingConfig: any = {};

            if (routingStrategy === 'header' || routingStrategy === 'header_match') {
                const headerName = (this.shadowRoot.querySelector('form-input[name="config_header_name"]') as any).value;
                if (headerName) routingConfig['header_name'] = headerName;
            } else if (routingStrategy === 'waterfall') {
                const reset = (this.shadowRoot.querySelector('form-select[name="config_waterfall_reset"]') as any).value;
                if (reset) routingConfig['limit_reset'] = reset;
            }

            let details: any = {
                routing: {
                    type: routingStrategy,
                    destinations: destinations,
                    config: Object.keys(routingConfig).length > 0 ? routingConfig : undefined,
                    weights: Object.keys(weights).length > 0 ? weights : undefined,
                    limits: Object.keys(limits).length > 0 ? limits : undefined,
                    mappings: Object.keys(mappings).length > 0 ? mappings : undefined
                }
            };

            if (enableRateLimit) {
                const limit = parseInt((this.shadowRoot.querySelector('form-input[name="rateLimit"]') as any).value);
                const rate = (this.shadowRoot.querySelector('form-select[name="rateWindow"]') as any).value;
                const identifierType = (this.shadowRoot.querySelector('form-select[name="identifierType"]') as any).value;
                const identifier = (this.shadowRoot.querySelector('form-input[name="identifier"]') as any).value;

                if (isNaN(limit) || limit <= 0) {
                    this.toastService.warning('Invalid rate limit. Must be a positive number.');
                    return;
                }

                    const multiplier = parseInt((this.shadowRoot.querySelector('form-input[name="multiplier"]') as any).value) || 1;
                    details.rate_limit = {
                        limit: limit,
                        rate: rate,
                        multiplier: multiplier,
                        identifier_type: identifierType,
                        identifier: identifier
                    };
            }

            const enableRetry = (this.shadowRoot.querySelector('form-toggle[name="enableRetry"]') as any).checked;
            let retryPolicy = undefined;
            if (enableRetry) {
                const retryOnMethods = (this.shadowRoot.querySelector('form-checkbox-group[name="retryOnMethods"]') as any).selectedValues;
                retryPolicy = {
                    enabled: true,
                    retry_on_methods: retryOnMethods
                };
            }

            if (retryPolicy) {
                details.retry_policy = retryPolicy;
            }

            // Sync plugins atomically via backend
            const inboundList = this.shadowRoot.querySelector('form-plugin-list[name="inbound_plugins"]') as any;
            const outboundList = this.shadowRoot.querySelector('form-plugin-list[name="outbound_plugins"]') as any;

            details.inbound_plugins = (inboundList?.values || []).filter((p: any) => p.plugin_id !== "");
            details.outbound_plugins = (outboundList?.values || []).filter((p: any) => p.plugin_id !== "");

            const updatedRule: Rule = {
                name,
                path,
                methods,
                domain_id: this.domainId,
                active,
                details
            };

            await this.ruleService.update(this.editingRuleId, updatedRule as any);

            this.showModal = false;
            this.editMode = false;
            this.editingRuleId = null;
            await this.loadData();
        } catch (e: any) {
            this.toastService.error(`Failed to update rule: ${e.message}`);
        }
    }


    async handleDeleteRule(ruleId: string) {
        if (!confirm('Are you sure you want to delete this routing rule?')) {
            return;
        }
        try {
            await this.ruleService.delete(ruleId);
            await this.loadData();
        } catch (e: any) {
            this.toastService.error(`Failed to delete rule: ${e.message}`);
        }
    }

    render() {
        if (!this.shadowRoot) return;

        const renderTree = () => {
            if (!this.domain) return '';

            // Trie Node Definition
            interface TrieNode {
                segment: string;
                children: Record<string, TrieNode>;
                rule?: Rule;
                isRoot?: boolean;
            }

            // Build Trie
            const root: TrieNode = { segment: this.domain.host, children: {}, isRoot: true };

            this.rules.forEach(rule => {
                const parts = rule.path.split('/').filter(p => p.length > 0);
                let current = root;

                parts.forEach((part) => {
                    if (!current.children[part]) {
                        current.children[part] = { segment: '/' + part, children: {} };
                    }
                    current = current.children[part];
                });

                const methodKey = `__methods__${rule.id}`;
                current.children[methodKey] = { segment: 'METHODS', children: {}, rule: rule };
            });

            // Layout Calculation
            const levelWidth = 200;
            const rowHeight = 60;
            let maxDepth = 0;
            let currentY = 50;

            const finalLinks: any[] = [];
            const finalNodes: any[] = [];
            currentY = 50;

            const traverseAndRender = (node: TrieNode, x: number): number => {
                maxDepth = Math.max(maxDepth, x);

                if (node.segment === 'METHODS' && node.rule) {
                    const rule = node.rule;
                    const ruleY = currentY;
                    currentY += rowHeight;

                    const methods = rule.methods || [];
                    let label = methods.length > 0 ? methods.join(', ') : 'ALL';
                    const fullLabel = label;

                    if (methods.length > 3) {
                        label = `${methods.slice(0, 2).join(', ')} +${methods.length - 2}`;
                    }
                    if (label.length > 20) {
                        label = label.substring(0, 19) + '…';
                    }

                    finalNodes.push({
                        id: `m_${rule.id}`,
                        x: x,
                        y: ruleY,
                        label: label,
                        fullLabel: fullLabel,
                        type: 'method'
                    });

                    const targetX = x + levelWidth;
                    let targetLabel = rule.details.routing?.destinations?.[0];
                    let isPassthrough = false;

                    if (!targetLabel && (this.domain?.details?.routing?.destinations?.length ?? 0) > 0) {
                        targetLabel = this.domain?.details?.routing?.destinations[0];
                        isPassthrough = true;
                    }

                    targetLabel = targetLabel || 'N/A';

                    try {
                        if (targetLabel.startsWith('http')) {
                            const url = new URL(targetLabel);
                            targetLabel = url.host + (url.pathname !== '/' ? url.pathname : '');
                            if (targetLabel.length > 25) { targetLabel = targetLabel.substring(0, 22) + '...'; }
                        }
                    } catch (e) { }

                    let extraInfo = '';
                    if (rule.details.rate_limit) { extraInfo = `[${rule.details.rate_limit.limit}/${rule.details.rate_limit.rate}]`; }

                    finalNodes.push({ x: targetX, y: ruleY, label: targetLabel, name: rule.name, subLabel: extraInfo, type: 'target', id: rule.id, active: rule.active });
                    finalLinks.push({ x1: x, y1: ruleY, x2: targetX, y2: ruleY });

                    maxDepth = Math.max(maxDepth, targetX + 200);

                    return ruleY;
                }

                const childrenKeys = Object.keys(node.children);
                const childCenters: { y: number, x: number }[] = [];

                childrenKeys.forEach(key => {
                    const child = node.children[key];
                    const childX = x + levelWidth;
                    const childY = traverseAndRender(child, childX);
                    childCenters.push({ y: childY, x: childX });
                });

                let myY = 0;
                if (childCenters.length > 0) {
                    const minY = Math.min(...childCenters.map(c => c.y));
                    const maxY = Math.max(...childCenters.map(c => c.y));
                    myY = (minY + maxY) / 2;
                } else {
                    myY = currentY;
                    currentY += rowHeight;
                }

                finalNodes.push({ x: x, y: myY, label: node.segment, type: node.isRoot ? 'root' : 'path' });

                childCenters.forEach(child => {
                    finalLinks.push({ x1: x, y1: myY, x2: child.x, y2: child.y });
                });

                return myY;
            };

            traverseAndRender(root, 50);

            const canvasHeight = Math.max(450, currentY + 100);
            const canvasWidth = Math.max(900, maxDepth + 100);

            return `
            <div class="map-container" style="overflow: hidden; cursor: grab; position: relative; height: 600px;">
                <svg id="mapSvg" width="100%" height="100%" viewBox="0 0 ${canvasWidth} ${canvasHeight}" style="background: #0f172a;">
                    <defs>
                        <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                            <polygon points="0 0, 10 3.5, 0 7" fill="#475569" />
                        </marker>
                    </defs>
                    <g id="mapContent" transform="translate(${this.pointX},${this.pointY}) scale(${this.scale})">
                        ${finalLinks.map(l => `<path d="M ${l.x1} ${l.y1} C ${(l.x1 + l.x2) / 2} ${l.y1}, ${(l.x1 + l.x2) / 2} ${l.y2}, ${l.x2} ${l.y2}" stroke="#475569" stroke-width="2" fill="none" marker-end="url(#arrowhead)" />`).join('')}
                        ${finalNodes.map(n => {
                const nameWidth = n.name ? n.name.length * 7 + 20 : 0;
                const labelWidth = n.label.length * 8 + 20;
                const subLabelWidth = n.subLabel ? n.subLabel.length * 7 + 20 : 0;
                const nodeWidth = Math.max(nameWidth, labelWidth, subLabelWidth);

                const isTarget = n.type === 'target';
                const textAnchor = isTarget ? 'start' : 'middle';
                const textX = isTarget ? 10 : nodeWidth / 2;

                const destinationText = n.details?.routing?.destinations?.[0] || (n.type === 'target' ? 'Passthrough' : '');
                const displayText = destinationText.length > 25 ? destinationText.substring(0, 22) + '...' : destinationText;
                const ruleName = (n as any).name || '';

                return `
                                <g transform="translate(${n.x}, ${n.y})" 
                                   class="${n.type === 'target' ? 'node-target' : ''}" 
                                   data-rule-id="${n.id || ''}"
                                   style="${n.type === 'target' ? 'cursor: pointer;' : ''}">
                                    <title>${n.fullLabel || n.label}</title>
                                    <rect x="0" y="-20" width="${nodeWidth}" height="${n.subLabel ? (n.name ? 52 : 42) : (n.name ? 38 : 32)}" rx="6" ry="6"
                                        fill="${n.type === 'root' ? 'var(--accent)' : (n.type === 'target' ? '#0f172a' : '#1e293b')}"
                                        stroke="${n.type === 'root' ? 'none' : (n.type === 'target' ? 'var(--accent)' : 'rgba(255,255,255,0.1)')}"
                                        stroke-width="${n.type === 'target' ? '2' : '1'}" />
                                    <text x="${textX}" y="${n.name ? '-7.5' : '0'}" 
                                        text-anchor="${textAnchor}"
                                        fill="${n.type === 'root' ? 'black' : 'white'}" 
                                        font-size="12px" font-family="monospace" font-weight="600">
                                        ${n.name || n.label}
                                    </text>
                                    ${n.name ? `
                                        <text x="${textX}" y="5.5" 
                                            text-anchor="${textAnchor}"
                                            fill="#64748b" 
                                            font-size="10px" font-family="monospace">
                                            ${n.label}
                                        </text>
                                    ` : ''}
                                    ${n.subLabel ? `
                                        <text x="${textX}" y="${n.name ? '19.5' : '15'}" 
                                            text-anchor="${textAnchor}"
                                            fill="#f59e0b" 
                                            font-size="10px" font-family="monospace">
                                            ${n.subLabel}
                                        </text>
                                    ` : ''}
                                    ${n.type === 'target' ? `
                                        <circle cx="${nodeWidth - 8}" 
                                                cy="-12" r="4" 
                                                fill="${n.active ? '#10b981' : '#ef4444'}" 
                                                stroke="#0f172a" stroke-width="1" />
                                    ` : ''}
                                </g>
                            `;
            }).join('')}
                    </g>
                </svg>

                <div class="map-controls">
                    <app-button variant="white" size="xs" id="btnZoomIn" icon="plus"></app-button>
                    <app-button variant="white" size="xs" id="btnZoomOut" icon="minus"></app-button>
                    <div class="map-controls-sep"></div>
                    <app-button variant="white" size="xs" id="btnReset">Reset</app-button>
                </div>
            </div>
        `;
        };

        const editingRule = this.editMode ? this.rules.find(r => r.id === this.editingRuleId) : null;

        this.shadowRoot.innerHTML = `
            ${injectAesthetic(this.seed)}
                    <style>
                :host { display: block; color: white; }
                .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 2rem; }
                
                h1 { font-size: 1.5rem; font-weight: 700; margin: 0; color: var(--accent, #f59e0b); text-transform: uppercase; }
                .icon-btn-header {
                    background: rgba(255, 255, 255, 0.03);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    color: #94a3b8;
                    cursor: pointer;
                    width: 42px;
                    height: 42px;
                    border-radius: 10px;
                    display: grid;
                    place-items: center;
                    transition: all 0.2s;
                }
                .icon-btn-header:hover:not(:disabled) {
                    background: rgba(255, 255, 255, 0.08);
                    color: white;
                    border-color: rgba(255, 255, 255, 0.2);
                    transform: translateY(-1px);
                }
                .icon-btn-header.primary {
                    background: rgba(245, 158, 11, 0.1);
                    border-color: rgba(245, 158, 11, 0.2);
                    color: var(--accent, #f59e0b);
                }
                .icon-btn-header.primary:hover:not(:disabled) {
                    background: var(--accent, #f59e0b);
                    color: black;
                    border-color: var(--accent);
                    box-shadow: 0 0 20px rgba(245, 158, 11, 0.3);
                }
                .icon-btn-header:disabled {
                    opacity: 0.3;
                    cursor: not-allowed;
                }

                /* Scrollbar Styles */
                ::-webkit-scrollbar {
                    width: 12px;
                    height: 12px;
                }

                ::-webkit-scrollbar-track {
                    background: rgba(0, 0, 0, 0.2);
                    border-left: 1px solid var(--border-sketch, rgba(255,255,255,0.1));
                }

                ::-webkit-scrollbar-thumb {
                    background: rgba(245, 158, 11, 0.2);
                    border-radius: 20px;
                    border: 3px solid transparent;
                    background-clip: content-box;
                }

                ::-webkit-scrollbar-thumb:hover {
                    background: rgba(245, 158, 11, 0.5);
                    background-clip: content-box;
                }

                .view-toggle {
                    display: flex; 
                    gap: 0.25rem; 
                    background: rgba(255, 255, 255, 0.05); 
                    padding: 2px; 
                    border-radius: 8px;
                }
                .toggle-btn {
                    height: 28px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: transparent; 
                    border: none; 
                    color: #94a3b8; 
                    padding: 0 0.75rem; 
                    border-radius: 6px; 
                    cursor: pointer; 
                    font-size: 0.75rem; 
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    transition: all 0.2s;
                }
                .toggle-btn.active { 
                    background: var(--accent); 
                    color: black; 
                }

                /* Table Styles */
                .table-container { background: rgba(255, 255, 255, 0.02); border: 1px solid rgba(255, 255, 255, 0.05); border-radius: 12px; overflow-x: auto; }
                table { width: 100%; border-collapse: collapse; text-align: left; table-layout: fixed; min-width: 1000px; }
                th { text-align: left; padding: 0.75rem 1rem; color: #94a3b8; font-weight: 500; font-size: 0.85rem; border-bottom: 1px solid rgba(255, 255, 255, 0.1); }
                td { padding: 0.6rem 1rem; border-top: 1px solid rgba(255, 255, 255, 0.05); font-size: 0.9rem; overflow: hidden; }
                .rule-name { font-weight: 600; color: white; }
                .rule-path { font-family: 'JetBrains Mono', monospace; color: var(--accent, #f59e0b); background: rgba(245, 158, 11, 0.05); padding: 0.2rem 0.4rem; border-radius: 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: block; }
                .rule-target { display: flex; align-items: center; gap: 0.5rem; font-family: monospace; }
                
                .action-group { display: flex; gap: 0.5rem; }
                .btn-icon {
                    background: transparent;
                    border: 1px solid transparent;
                    color: #94a3b8;
                    padding: 0.25rem;
                    border-radius: 4px;
                    cursor: pointer;
                    display: grid;
                    place-items: center;
                    transition: all 0.2s;
                }
                .btn-icon:hover { color: white; background: rgba(255,255,255,0.1); }
                .btn-icon.delete:hover { color: #ef4444; background: rgba(239, 68, 68, 0.1); }

                /* Helper class for hiding strict mode toggle */
                .hidden { display: none; }

                .section-title { font-size: 1rem; color: var(--accent, #f59e0b); margin: 1.5rem 0 1rem 0; font-weight: 600; text-transform: uppercase; border-bottom: 1px solid rgba(255, 255, 255, 0.1); padding-bottom: 0.5rem; }

                /* Map Styles */
                .map-container {
                    background: rgba(255, 255, 255, 0.02); 
                    border: 1px solid rgba(255, 255, 255, 0.05); 
                    border-radius: 12px; 
                    overflow: auto; 
                    padding: 0;
                    display: flex; 
                    justify-content: center;
                    position: relative;
                }

                .loader { width: 40px; height: 40px; border: 3px solid rgba(245, 158, 11, 0.1); border-top-color: var(--accent, #f59e0b); border-radius: 50%; animation: spin 1s linear infinite; margin: 2rem auto; }

                .control-bar { 
                    display: flex; 
                    align-items: center; 
                    margin-bottom: 2rem; 
                    background: rgba(255, 255, 255, 0.02); 
                    padding: 0.5rem 0.75rem; 
                    border-radius: 10px; 
                    border: 1px solid rgba(255, 255, 255, 0.05);
                }

                .map-controls {
                    position: absolute;
                    bottom: 12px;
                    right: 12px;
                    display: flex;
                    gap: 8px;
                    background: rgba(255, 255, 255, 0.03);
                    backdrop-filter: blur(10px);
                    padding: 6px;
                    border-radius: 12px;
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
                    z-index: 10;
                    align-items: center;
                }

                .map-controls-sep {
                    width: 1px;
                    height: 20px;
                    background: rgba(255, 255, 255, 0.1);
                    margin: 0 4px;
                }
</style>

    <div class="control-bar">
        <app-button variant="white" size="sm" id="btn-back" icon="arrow-left"></app-button>
        
        <div style="flex: 1;"></div>

        <div style="display: flex; gap: 0.75rem; align-items: center;">
            <div class="view-toggle">
                <button class="toggle-btn ${this.viewMode === 'list' ? 'active' : ''}" id="viewList">List</button>
                <button class="toggle-btn ${this.viewMode === 'map' ? 'active' : ''}" id="viewMap">Map</button>
                <button class="toggle-btn ${this.viewMode === 'code' ? 'active' : ''}" id="viewCode">Code</button>
            </div>
            <app-button variant="white" size="sm" id="addRuleBtn" icon="plus"></app-button>
        </div>
    </div>

            ${this.isLoading ? '<div class="loader"></div>' : ''}

            ${this.viewMode === 'list' ? `
                <div class="table-container">
                    <table>
                        <thead>
                            <tr>
                                <th style="width: 40px; text-align: center;"></th>
                                <th style="width: 150px;">Name</th>
                                <th>Path</th>
                                <th style="width: 150px;">Routing</th>
                                <th style="width: 160px;">Policy</th>
                                <th style="width: 80px;">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${this.rules.map(rule => `
                                <tr>
                                    <td style="text-align: center; padding-right: 0;">
                                        <div data-tooltip="${rule.active ? 'Active' : 'Inactive'}" style="width: 8px; height: 8px; border-radius: 50%; background: ${rule.active ? '#10b981' : '#475569'}; margin: 0 auto; ${rule.active ? 'box-shadow: 0 0 10px rgba(16, 185, 129, 0.4);' : ''}"></div>
                                    </td>
                                    <td>
                                        <div style="display: flex; gap: 0.5rem; align-items: center;">
                                            <div class="rule-name">${rule.name}</div>
                                        </div>
                                    </td>
                                    <td><span class="rule-path" style="font-family: monospace; color: #38bdf8;">${rule.path}</span></td>
                                    <td>
                                        <div class="rule-routing">
                                            <div style="font-weight: 600; font-size: 0.75rem; color: #94a3b8; text-transform: uppercase; margin-bottom: 0.25rem;">${rule.details.routing?.type || 'ROUND_ROBIN'}</div>
                                            <div class="rule-target" style="display: flex; align-items: center; gap: 0.35rem;">
                                                <app-icon name="box" size="xs" style="color: #94a3b8;"></app-icon>
                                                ${(rule.details.routing?.destinations?.join(', ') || '-')}
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div style="display: flex; flex-direction: column; gap: 0.25rem;">
                                            <div style="display: flex; gap: 0.35rem; flex-wrap: wrap;">
                                                ${(rule.methods?.map(m => `
                                                    <span style="font-size: 0.65rem; padding: 0.1rem 0.35rem; background: rgba(56, 189, 248, 0.1); color: #38bdf8; border-radius: 3px; font-weight: 600; font-family: monospace;">${m}</span>
                                                `).join('') || '<span style="color: #64748b; font-size: 0.7rem;">ALL</span>')}
                                            </div>
                                            <div style="display: flex; gap: 0.75rem; align-items: center; font-size: 0.75rem; color: #64748b;">
                                                <div data-tooltip="Inbound Plugins" style="display: flex; align-items: center; gap: 0.25rem;">
                                                    <app-icon name="shield" size="xs"></app-icon>
                                                    ${rule.details?.inbound_plugins?.length || 0}
                                                </div>
                                                <div data-tooltip="Outbound Plugins" style="display: flex; align-items: center; gap: 0.25rem;">
                                                    <app-icon name="external-link" size="xs"></app-icon>
                                                    ${rule.details?.outbound_plugins?.length || 0}
                                                </div>
                                                ${rule.details?.rate_limit ? `
                                                    <div data-tooltip="Rate Limit" style="color: #f59e0b; display: flex; align-items: center; gap: 0.25rem;">
                                                        <app-icon name="clock" size="xs"></app-icon>
                                                        ${rule.details.rate_limit.limit}/${rule.details.rate_limit.rate}
                                                    </div>
                                                ` : ''}
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div class="action-group">
                                            <app-button variant="ghost" size="xs" class="btn-edit-list" data-rule-id="${rule.id}" icon="edit" data-tooltip="Edit Rule"></app-button>
                                            <app-button variant="ghost" size="xs" class="btn-delete-list" data-delete-id="${rule.id}" icon="trash" data-tooltip="Delete Rule" style="color: #ef4444;"></app-button>
                                        </div>
                                    </td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                    ${!this.isLoading && this.rules.length === 0 ? `
                        <div style="padding: 3rem; text-align: center; color: #475569;">
                            No rules defined for this domain. Create one to start routing traffic.
                        </div>
                    ` : ''}
                </div>

            ` : (this.viewMode === 'map' ? renderTree() : this.renderCodeView())
            }

    <!-- Create/Edit Rule Modal -->
    <app-modal id="ruleModal" ${this.showModal ? 'open' : ''}>
        <div slot="header" style="display: flex; align-items: center; justify-content: space-between; width: 100%;">
            <h2 style="margin: 0; color: var(--accent);">${this.editMode ? 'Edit Rule' : 'New Rule'}</h2>
            <div style="margin-left: auto;">
                <form-toggle name="active" label="Active" style="margin-bottom: 0;" ${this.rules.find(r => r.id === this.editingRuleId)?.active ?? true ? 'checked' : ''}></form-toggle>
            </div>
        </div>

        <form id="ruleForm">
             <form-input name="name" label="Rule Name" placeholder="api-v1-users" required 
                value="${this.editMode ? this.rules.find(r => r.id === this.editingRuleId)?.name || '' : ''}"></form-input>
             
             <form-input name="path" label="Path Pattern" placeholder="/api/v1/users" required 
                value="${this.editMode ? this.rules.find(r => r.id === this.editingRuleId)?.path || '' : ''}"></form-input>

             <form-checkbox-group name="methods" label="HTTP Methods *" 
                options='["GET", "POST", "PUT", "DELETE", "PATCH", "HEAD", "OPTIONS"]'
                values='${JSON.stringify(this.editMode ? (this.rules.find(r => r.id === this.editingRuleId)?.methods || []) : [])}'></form-checkbox-group>

             <div class="section-title">Backend Routing</div>
             
             <!-- Routing Strategy -->
             <form-select id="routingStrategy" name="routingStrategy" label="Routing Algorithm" 
                 options='[
                     {"value":"round_robin","label":"Round Robin (Default)"},
                      {"value":"weighted_round_robin","label":"Weighted Round Robin"},
                      {"value":"waterfall","label":"Waterfall (Priority)"},
                      {"value":"header","label":"Header Based (Sticky)"},
                      {"value":"header_match","label":"Header Match (Explicit)"},
                      {"value":"power_of_two_choices","label":"Power of Two Choices (Least Loaded)"}
                 ]'
                 value="${this.editMode ? (this.rules.find(r => r.id === this.editingRuleId)?.details?.routing?.type || 'round_robin') : 'round_robin'}"></form-select>

             ${(() => {
                const rule = this.editMode ? this.rules.find(r => r.id === this.editingRuleId) : null;
                const destinations = rule?.details?.routing?.destinations || [];
                const weights = rule?.details?.routing?.weights || {};
                const limits = rule?.details?.routing?.limits || {};
                const mappings = rule?.details?.routing?.mappings || {};

                // Merge destinations and weights for the form
                const formValues = destinations.map(dest => {
                    return {
                        value: dest,
                        weight: weights[dest] !== undefined ? weights[dest] : 1,
                        limit: limits[dest] !== undefined ? limits[dest] : 0,
                        mapping: mappings[dest] !== undefined ? mappings[dest] : ''
                    };
                });

                const isWeighted = (rule?.details?.routing?.type === 'weighted_round_robin');
                const isWaterfall = (rule?.details?.routing?.type === 'waterfall');

                return `<form-dynamic-list 
                            id="destinationsList"
                            name="destination" 
                            label="Target Destinations (Optional - defaults to passthrough)" 
                            placeholder="http://backend:8080/users" 
                            values='${JSON.stringify(formValues)}'
                            ${isWeighted ? 'with-weights' : ''}
                            ${isWaterfall ? 'with-limits' : ''}
                            ${rule?.details?.routing?.type === 'header_match' ? 'with-mappings' : ''}
                        ></form-dynamic-list>`;
            })()}

             <!-- Dynamic Routing Config Fields -->
             <div id="routingConfigContainer">
                <div id="config_header" style="display: none; padding-left: 1rem; border-left: 2px solid var(--accent, #f59e0b); margin-bottom: 1rem;">
                    <form-input name="config_header_name" label="Header Name" placeholder="X-User-ID" 
                        value="${this.editMode ? (this.rules.find(r => r.id === this.editingRuleId)?.details?.routing?.config?.['header_name'] || 'X-Routing-Key') : 'X-Routing-Key'}"></form-input>
                </div>
                <!-- Waterfall Config -->
                <div id="config_waterfall" style="display: none; padding-left: 1rem; border-left: 2px solid var(--accent, #f59e0b); margin-bottom: 1rem;">
                    <form-select name="config_waterfall_reset" label="Limit Reset Frequency"
                        options='[{"value":"minute","label":"Every Minute"},{"value":"hour","label":"Hourly"},{"value":"day","label":"Daily"},{"value":"month","label":"Monthly"}]'
                        value="${this.editMode ? (this.rules.find(r => r.id === this.editingRuleId)?.details?.routing?.config?.['limit_reset'] || 'hour') : 'hour'}"></form-select>
                        <div style="font-size: 0.8rem; color: #94a3b8; margin-top: 0.5rem; line-height: 1.4;">
                            Requests to the primary destination are counted. Once the limit is reached, traffic overflows to the next destination.
                        </div>
                </div>
             </div>


             <div class="section-title">Plugins</div>

             <form-plugin-list 
                name="inbound_plugins" 
                label="Request Transformations" 
                all-plugins='${JSON.stringify(this.allPlugins)}'
                values='${JSON.stringify((this.requestPlugins || []).map(p => ({ plugin_id: p.id, config: p.config })))}'
             ></form-plugin-list>

             <form-plugin-list 
                name="outbound_plugins" 
                label="Response Transformations" 
                all-plugins='${JSON.stringify(this.allPlugins)}'
                values='${JSON.stringify((this.responsePlugins || []).map(p => ({ plugin_id: p.id, config: p.config })))}'
             ></form-plugin-list>

             <div class="section-title">Rate Limiting</div>

             <form-toggle id="enableRateLimit" name="enableRateLimit" label="Enable Rate Limiting" 
                ${this.editMode && this.rules.find(r => r.id === this.editingRuleId)?.details?.rate_limit ? 'checked' : ''}></form-toggle>

             <div id="rateLimitFields" style="display: ${this.editMode && this.rules.find(r => r.id === this.editingRuleId)?.details?.rate_limit ? 'block' : 'none'}; padding-left: 1.5rem; border-left: 2px solid rgba(255,255,255,0.1); margin-top: 1rem;">
                <form-input type="number" name="rateLimit" label="Request Limit" min="1" placeholder="e.g. 100"
                    value="${this.editMode ? this.rules.find(r => r.id === this.editingRuleId)?.details?.rate_limit?.limit || '' : ''}"></form-input>
                
                <form-select name="rateWindow" label="Time Window" 
                    options='[{"value":"second","label":"Per Second"},{"value":"minute","label":"Per Minute"},{"value":"hour","label":"Per Hour"},{"value":"day","label":"Per Day"}]'
                    value="${this.editMode ? this.rules.find(r => r.id === this.editingRuleId)?.details?.rate_limit?.rate || 'minute' : 'minute'}"></form-select>

                <form-input type="number" name="multiplier" label="Window Multiplier" min="1" placeholder="e.g. 1 (Default)"
                    value="${this.editMode ? this.rules.find(r => r.id === this.editingRuleId)?.details?.rate_limit?.multiplier || 1 : 1}"></form-input>
                
                <form-select name="identifierType" label="Identifier Location"
                    options='[{"value":"header","label":"Request Header"},{"value":"query","label":"Query Parameter"},{"value":"cookie","label":"Cookie"},{"value":"ip","label":"IP Address"},{"value":"referer","label":"Referer"},{"value":"body","label":"Request Body"},{"value":"path","label":"URL Path"}]'
                    value="${this.editMode ? this.rules.find(r => r.id === this.editingRuleId)?.details?.rate_limit?.identifier_type || 'ip' : 'ip'}"></form-select>

                <form-input name="identifier" label="Identifier Key" placeholder="e.g. X-API-Key or api_key"
                    value="${this.editMode ? this.rules.find(r => r.id === this.editingRuleId)?.details?.rate_limit?.identifier || '' : ''}"></form-input>
             </div>

             <div class="section-title">Reliability</div>

             <form-toggle id="enableRetry" name="enableRetry" label="Enable Automatic Retries"
                ${this.editMode && this.rules.find(r => r.id === this.editingRuleId)?.details?.retry_policy?.enabled ? 'checked' : ''}></form-toggle>

             <div id="retrySettings" style="display: ${this.editMode && this.rules.find(r => r.id === this.editingRuleId)?.details?.retry_policy?.enabled ? 'block' : 'none'}; padding-left: 1.5rem; border-left: 2px solid rgba(255,255,255,0.1); margin-top: 1rem;">
                <div style="background: rgba(245, 158, 11, 0.1); border: 1px solid rgba(245, 158, 11, 0.2); padding: 0.75rem; border-radius: 6px; margin-bottom: 1rem; font-size: 0.85rem; color: #cbd5e1;">
                    <strong style="color: var(--accent, #f59e0b);">Note:</strong> By default, only idempotent methods (GET, HEAD, OPTIONS) are retried on failure. Enabling other methods may cause side effects if your backend is not idempotent.
                </div>
                
                <form-checkbox-group name="retryOnMethods" label="Additional Retry Methods" 
                    options='["POST", "PUT", "DELETE", "PATCH"]'
                    values='${JSON.stringify(this.editMode ? (this.rules.find(r => r.id === this.editingRuleId)?.details?.retry_policy?.retry_on_methods || []) : [])}'></form-checkbox-group>
             </div>
        </form>

        <div slot="actions">
            <div style="display: flex; gap: 0.5rem; justify-content: flex-end; width: 100%;">
                ${this.editMode ? `
                    <app-button variant="ghost" size="sm" id="deleteRuleBtn" icon="trash" data-tooltip="Delete Rule" style="color: #ef4444; margin-right: auto;"></app-button>
                ` : ''}
                <app-button variant="ghost" size="sm" id="cancelBtn" icon="x" data-tooltip="Cancel"></app-button>
                <app-button variant="primary" size="sm" id="saveRuleBtn" icon="check" data-tooltip="${this.editMode ? 'Save Changes' : 'Create Rule'}"></app-button>
            </div>
        </div>
    </app-modal>

    <app-save-bar id="yamlSaveBar" message="Careful — you have unsaved changes to your rules!"></app-save-bar>

                                    `;

        // Add listener for rate limit toggle
        this.shadowRoot.getElementById('enableRateLimit')?.addEventListener('change', (e) => {
            const fields = this.shadowRoot?.getElementById('rateLimitFields');
            if (fields) {
                fields.style.display = (e.target as HTMLInputElement).checked ? 'block' : 'none';
            }
        });

        // Add listener for retry policy toggle
        this.shadowRoot.getElementById('enableRetry')?.addEventListener('change', (e) => {
            const settings = this.shadowRoot?.getElementById('retrySettings');
            if (settings) {
                settings.style.display = (e.target as HTMLInputElement).checked ? 'block' : 'none';
            }
        });

        // Add listener for Routing Strategy toggle
        const strategySelect = this.shadowRoot.getElementById('routingStrategy');
        const updateRoutingVisibility = () => {
            const val = (strategySelect as any)?.value || 'round_robin';
            const container = this.shadowRoot?.getElementById('routingConfigContainer');
            const destList = this.shadowRoot?.getElementById('destinationsList');
            const headerConfig = this.shadowRoot?.getElementById('config_header');

            if (destList) {
                if (val === 'weighted_round_robin') {
                    destList.setAttribute('with-weights', '');
                    destList.removeAttribute('with-limits');
                    destList.removeAttribute('with-mappings');
                } else if (val === 'waterfall') {
                    destList.setAttribute('with-limits', '');
                    destList.removeAttribute('with-weights');
                    destList.removeAttribute('with-mappings');
                } else if (val === 'header_match') {
                    destList.setAttribute('with-mappings', '');
                    destList.removeAttribute('with-weights');
                    destList.removeAttribute('with-limits');
                } else {
                    destList.removeAttribute('with-weights');
                    destList.removeAttribute('with-limits');
                    destList.removeAttribute('with-mappings');
                }
            }

            if (!container) return;

            container.querySelectorAll('div[id^="config_"]').forEach((el: any) => el.style.display = 'none');

            if (headerConfig) {
                if (val === 'header' || val === 'header_match') {
                    headerConfig.style.display = 'block';
                } else {
                    headerConfig.style.display = 'none';
                }
            }

            const target = this.shadowRoot?.getElementById(`config_${val}`);
            if (target) {
                target.style.display = 'block';
            }
        };
        if (strategySelect) {
            setTimeout(updateRoutingVisibility, 0);
            strategySelect.addEventListener('change', updateRoutingVisibility);
            strategySelect.addEventListener('input', updateRoutingVisibility);
        }

        this.setupEventListeners();
        this.updateYamlButtons();
    }

    private renderCodeView() {
        return `
            <div class="code-editor-wrapper" style="display: flex; flex-direction: column; gap: 1rem; height: calc(100vh - 250px); min-height: 500px;">
                <code-editor id="yamlEditor" value="${this.currentYaml.replace(/"/g, '&quot;')}"></code-editor>
            </div>
        `;
    }

    setupEventListeners() {
        const shadow = this.shadowRoot;
        if (!shadow) return;

        shadow.getElementById('btn-back')?.addEventListener('click', () => {
            this.dispatchEvent(new CustomEvent('back', { bubbles: true, composed: true }));
        });

        // View Toggles
        shadow.getElementById('viewList')?.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (this.viewMode !== 'list') {
                this.viewMode = 'list';
                this.showModal = false;
                this.render();
            }
        });

        shadow.getElementById('viewMap')?.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (this.viewMode !== 'map') {
                this.viewMode = 'map';
                this.showModal = false;
                this.render();
            }
        });

        shadow.getElementById('viewCode')?.addEventListener('click', async (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (this.viewMode !== 'code') {
                this.viewMode = 'code';
                this.showModal = false;
                await this.loadData();
                this.render();
            }
        });


        // Save Yaml from Editor (Bulk Update)
        // Save Yaml from Editor (Bulk Update) via Save Bar
        const yamlSaveBar = shadow.getElementById('yamlSaveBar') as any;
        yamlSaveBar?.addEventListener('save', async () => {
            const editor = shadow.getElementById('yamlEditor') as any;
            if (!editor) return;
            if (!this.domainId) return;

            try {
                this.isLoading = true;
                this.updateYamlButtons();
                
                await this.domainService.updateConfig(this.domainId, editor.value);
                this.toastService.success('Configuration updated successfully');
                await this.loadData();
            } catch (err: any) {
                this.toastService.error(`Update failed: ${err.message}`);
            } finally {
                this.isLoading = false;
                this.updateYamlButtons();
            }
        });

        yamlSaveBar?.addEventListener('cancel', () => {
            this.currentYaml = this.originalYaml;
            const editor = shadow.getElementById('yamlEditor') as any;
            if (editor) editor.value = this.currentYaml;
            this.updateYamlButtons();
        });

        const yamlEditor = shadow.getElementById('yamlEditor');
        const handleYamlChange = (e: any) => {
            this.currentYaml = e.detail?.value || e.target?.value || "";
            this.updateYamlButtons();
        };
        yamlEditor?.addEventListener('change', handleYamlChange);
        yamlEditor?.addEventListener('input', handleYamlChange);



        // Add button
        shadow.getElementById('addRuleBtn')?.addEventListener('click', () => {
            this.editMode = false;
            this.editingRuleId = null;
            this.requestPlugins = [];
            this.responsePlugins = [];
            this.showModal = true;
            this.render();
        });

        shadow.querySelectorAll('.btn-edit-list, .node-target').forEach(el => {
            el.addEventListener('click', async (e) => {
                const ruleId = (el as HTMLElement).getAttribute('data-edit-id') || (el as HTMLElement).getAttribute('data-rule-id');
                if (ruleId) {
                    this.editMode = true;
                    this.editingRuleId = ruleId;
                    this.showModal = true;
                    this.requestPlugins = []; // Reset while loading
                    this.responsePlugins = [];
                    this.render();

                    try {
                        const rule = this.rules.find(r => r.id === ruleId);
                        if (rule && rule.details) {
                            // Map existing plugins from details
                            this.requestPlugins = (rule.details.inbound_plugins || []).map(ip => {
                                const p = this.allPlugins.find(ap => ap.id === ip.plugin_id);
                                return p ? { ...p, config: ip.config } : null;
                            }).filter(p => p !== null) as Plugin[];

                            this.responsePlugins = (rule.details.outbound_plugins || []).map(op => {
                                const p = this.allPlugins.find(ap => ap.id === op.plugin_id);
                                return p ? { ...p, config: op.config } : null;
                            }).filter(p => p !== null) as Plugin[];
                        }
                        this.render();
                    } catch (e) {
                        this.toastService.error('Failed to load rule plugins');
                    }
                }
            });
        });

        // Delete button in modal
        shadow.getElementById('deleteRuleBtn')?.addEventListener('click', async (e) => {
            e.stopPropagation();
            if (this.editingRuleId) {
                await this.handleDeleteRule(this.editingRuleId);
                this.showModal = false;
                this.render();
            }
        });

        // Cancel button
        shadow.getElementById('cancelBtn')?.addEventListener('click', () => {
            this.showModal = false;
            this.editMode = false;
            this.editingRuleId = null;
            this.render();
        });

        // Close modal on overlay click
        shadow.getElementById('modalOverlay')?.addEventListener('click', (e) => {
            if (e.target === e.currentTarget) {
                this.showModal = false;
                this.editMode = false;
                this.editingRuleId = null;
                this.render();
            }
        });

        // Map Interaction Listeners
        const svg = shadow.getElementById('mapSvg');
        const container = shadow.querySelector('.map-container') as HTMLElement;

        if (svg && container) {
            svg.addEventListener('mousedown', (e) => {
                // Only start drag if not clicking a node
                e.preventDefault();
                this.panning = true;
                this.startX = (e as MouseEvent).clientX;
                this.startY = (e as MouseEvent).clientY;
                container.style.cursor = 'grabbing';
            });

            svg.addEventListener('wheel', (e) => {
                e.preventDefault();
                const wheelEvent = e as WheelEvent;

                const svgEl = e.currentTarget as SVGSVGElement;
                const CTM = svgEl.getScreenCTM();
                if (!CTM) return;
                const inv = CTM.inverse();

                // Mouse point in SVG units
                const pt = svgEl.createSVGPoint();
                pt.x = wheelEvent.clientX;
                pt.y = wheelEvent.clientY;
                const svgP = pt.matrixTransform(inv);

                const delta = -wheelEvent.deltaY;
                // Final refined zoom factor - increasing to 1.01 per user request
                const factor = Math.pow(1.01, delta);

                // Limit scale
                const newScale = Math.min(Math.max(0.1, this.scale * factor), 10);

                // Zoom math: p_new = svgP - (svgP - p_old) * (s_new / s_old)
                const ratio = newScale / this.scale;
                this.pointX = svgP.x - (svgP.x - this.pointX) * ratio;
                this.pointY = svgP.y - (svgP.y - this.pointY) * ratio;
                this.scale = newScale;
                this.updateTransform();
            });

            shadow.getElementById('btnZoomIn')?.addEventListener('click', () => this.zoomRelative(1.2));
            shadow.getElementById('btnZoomOut')?.addEventListener('click', () => this.zoomRelative(1 / 1.2));
            shadow.getElementById('btnReset')?.addEventListener('click', () => this.resetMap());
        }
        // Form submission
        shadow.getElementById('saveRuleBtn')?.addEventListener('click', () => {
            (shadow.getElementById('ruleForm') as HTMLFormElement)?.requestSubmit();
        });

        shadow.getElementById('ruleForm')?.addEventListener('submit', async (e) => {
            e.preventDefault();

            if (this.editMode) {
                await this.handleUpdateRule();
            } else {
                await this.handleCreateRule();
            }
        });

        // Delete buttons
        shadow.querySelectorAll('.btn-delete, .btn-delete-list').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                e.stopPropagation();
                const ruleId = (btn as HTMLElement).getAttribute('data-delete-id') || (btn as HTMLElement).getAttribute('data-id');
                if (ruleId) {
                    await this.handleDeleteRule(ruleId);
                }
            });
        });

        // Rate limit toggle listener
        shadow.getElementById('enableRateLimit')?.addEventListener('change', (e) => {
            const fields = shadow.getElementById('rateLimitFields');
            if (fields) {
                const isChecked = (e.target as any).checked;
                fields.style.display = isChecked ? 'block' : 'none';

                // Toggle required fields inside
                const requiredInputs = fields.querySelectorAll('form-input, form-select');
                requiredInputs.forEach((input: any) => {
                    if (isChecked) {
                        input.setAttribute('required', '');
                    } else {
                        input.removeAttribute('required');
                    }
                });
            }
        });

        const identifierSelect = shadow.querySelector('form-select[name="identifierType"]');
        const updateIdentifierVisibility = () => {
            const type = (identifierSelect as any)?.value || 'ip';
            const identifierField = shadow.querySelector('form-input[name="identifier"]') as any;
            if (identifierField) {
                if (type === 'ip') {
                    identifierField.style.display = 'none';
                    identifierField.removeAttribute('required');
                } else {
                    identifierField.style.display = 'block';
                    identifierField.setAttribute('required', '');
                }
            }
        };

        if (identifierSelect) {
            setTimeout(updateIdentifierVisibility, 0);
            identifierSelect.addEventListener('change', updateIdentifierVisibility);
        }
    }

    private updateYamlButtons() {
        const shadow = this.shadowRoot;
        if (!shadow) return;

        const saveBar = shadow.getElementById('yamlSaveBar') as any;
        const isDirty = this.currentYaml !== this.originalYaml;

        if (saveBar) {
            saveBar.active = isDirty && this.viewMode === 'code';
            saveBar.loading = this.isLoading;
        }
    }

    private zoomRelative(factor: number) {
        const svg = this.shadowRoot?.getElementById('mapSvg') as unknown as SVGSVGElement;
        if (!svg) return;

        const rect = svg.getBoundingClientRect();
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;

        const CTM = svg.getScreenCTM();
        if (!CTM) return;
        const inv = CTM.inverse();

        const pt = svg.createSVGPoint();
        pt.x = rect.left + centerX;
        pt.y = rect.top + centerY;
        const svgP = pt.matrixTransform(inv);

        const newScale = Math.min(Math.max(0.1, this.scale * factor), 10);
        const ratio = newScale / this.scale;
        
        this.pointX = svgP.x - (svgP.x - this.pointX) * ratio;
        this.pointY = svgP.y - (svgP.y - this.pointY) * ratio;
        this.scale = newScale;
        this.updateTransform();
    }

    private resetMap() {
        this.scale = 1;
        this.pointX = 0;
        this.pointY = 0;
        this.updateTransform();
    }
}

customElements.define('rule-management', RuleManagement);
